import { createExternalStore } from "@client/utils/createExternalStore";
import { useCallback, useEffect } from "react";

/**
 * Which right-hand app-bar drawer is open, if any.
 *
 * These panels are peers: each is a modal sheet with its own `z-50` scrim,
 * anchored to the same edge, opened from an icon sitting beside the others in
 * the app bar. Before this store each owned a private `useState`/module flag
 * and none knew about the rest, so opening one over another stacked two scrims
 * and left the funder dismissing panels one at a time to get back to the page.
 *
 * A single-slot store rather than a flag per drawer: "at most one open" is then
 * a property of the state's SHAPE, not an invariant every new panel has to
 * remember to maintain. Adding a fourth drawer means adding a name here and
 * reading this store — it cannot forget to close its siblings.
 *
 * The General Round Notes drawer keeps its own module store for WHICH
 * recommender it shows; that is payload, not open-state, so the two compose
 * (see `generalNotesStore.ts`).
 */
export type AppDrawerName = "queue" | "todo" | "generalNotes";

const store = createExternalStore<AppDrawerName | null>(null);

/** Open one drawer, closing whichever was open. */
export const openAppDrawer = (name: AppDrawerName) => store.set(name);

/** Close `name` — but only if it is the drawer actually open. */
export const closeAppDrawer = (name: AppDrawerName) => {
  if (store.get() === name) store.set(null);
};

export const closeAllAppDrawers = () => store.set(null);

export const getOpenAppDrawer = () => store.get();

/**
 * `[isOpen, setOpen]` for one drawer, shaped to drop straight into a `Sheet`'s
 * `open` / `onOpenChange`.
 *
 * `setOpen(true)` claims the slot (closing any sibling); `setOpen(false)`
 * releases it only if this drawer still holds it, so a closing sibling can
 * never clobber the drawer that just replaced it.
 */
export function useAppDrawer(
  name: AppDrawerName,
): [boolean, (open: boolean) => void] {
  const openDrawer = store.useValue();
  const setOpen = useCallback(
    (open: boolean) => {
      if (open) openAppDrawer(name);
      else closeAppDrawer(name);
    },
    [name],
  );

  // A drawer whose component has unmounted cannot be open.
  //
  // This slot used to be local `useState` inside each drawer, which got the
  // reset for free — leaving the round destroyed the state. Moving it to a
  // module store to make the panels mutually exclusive quietly dropped that,
  // so leaving a round with a drawer open and entering another one popped it
  // open again with no interaction. Releasing only OUR slot means a sibling
  // that took it meanwhile is left alone.
  useEffect(() => () => closeAppDrawer(name), [name]);

  return [openDrawer === name, setOpen];
}

/** Which drawer is open, for surfaces that must know they are covered. */
export function useAppDrawerName(): AppDrawerName | null {
  return store.useValue();
}
